import { PlainButton, Tooltip } from "@jugl-web/ui-components";
import { ErrorBoundary } from "@jugl-web/utils/utils/ErrorBoundary";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { useChatBot } from "@jugl-web/domain-resources/chat-bot";
import { AIAssistantButton } from "@web-src/modules/ai-assistant/AIAssistantButton";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { SingletonPopoverProvider } from "@web-src/modules/common/providers/SingletonPopoverProvider";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { TaskTimeTrackerNavbarButton } from "@web-src/modules/tasks/components/TaskTimeTrackerNavbarButton";
import { useTheme } from "@web-src/themes/useTheme";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { useCXSignupDialog } from "@web-src/modules/cx/components/CXSignupDialog/useCXSignupDialog";
import { OnboardingFlowTooltip } from "@web-src/modules/cx/components/OnboardingFlowTooltip";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { ReactComponent as LogoIcon } from "./assets/logo.svg";
import { ReactComponent as JuglCxLogoIcon } from "./assets/jugl-cx-logo.svg";
import { ReactComponent as UserIcon } from "./assets/user.svg";
import { ReactComponent as PricingIcon } from "./components/CXProfilePopover/assets/pricing.svg";
import { ReactComponent as SupportChatIcon } from "./components/CXProfilePopover/assets/support-chat.svg";
import { EntitySubscriptionInfoItem } from "./components/EntitySubscriptionInfoItem";
import { ProfilePopover } from "./components/ProfilePopover";
import { SelectEntityPopover } from "./components/SelectEntityPopover/SelectEntityPopover";
import { AnonymousSelectEntityPlaceholder } from "./components/AnonymousSelectEntityPlaceholder";
import { CXProfilePopover } from "./components/CXProfilePopover";

export const MainNavBar = () => {
  const { tcx } = useTheme();
  const { isCx } = useAppVariant();
  const { isTravelLogEmployee } = useUserRole();
  const { entity, subscriptionInfo } = useEntityProvider();
  const { isAnonymous } = useAnonymousSession();
  const cxSignupDialog = useCXSignupDialog();

  return (
    <SingletonPopoverProvider>
      <div
        className={tcx("h-[48px]", {
          light: "bg-primary-600",
          dark: "bg-[#181818]",
          blue: "bg-primary-800",
        })}
      >
        <div className="flex h-[48px] w-full items-center justify-between pr-6">
          <div className="flex h-full items-center justify-start gap-3">
            {isAnonymous ? (
              <AnonymousSelectEntityPlaceholder />
            ) : (
              <SelectEntityPopover />
            )}
          </div>
          <span className="flex items-center gap-4">
            {!isAnonymous && <TaskTimeTrackerNavbarButton />}
            <EntitySubscriptionInfoItem />
            {!isCx &&
              !isTravelLogEmployee &&
              entity &&
              subscriptionInfo?.planIsActive && (
                <ErrorBoundary fallback={null}>
                  <AIAssistantButton />
                </ErrorBoundary>
              )}
            {isCx && !isAnonymous && <CXSeePlansButton />}
            {isCx && <CXSupportChatButton />}
            {isCx && (
              <OnboardingFlowTooltip
                flow="postPurchase"
                step={1}
                placement="bottom-end"
              >
                <CXProfilePopover />
              </OnboardingFlowTooltip>
            )}
            {isAnonymous ? (
              <PlainButton
                onClick={() => cxSignupDialog?.open?.()}
                className={cx(
                  "flex h-8 items-center justify-center rounded-lg px-2 transition-colors",
                  cxSignupDialog && "hover:bg-[#333333]"
                )}
              >
                <UserIcon />
              </PlainButton>
            ) : (
              <ProfilePopover />
            )}
            <div className="ml-4 flex items-center gap-1">
              {isCx ? (
                <JuglCxLogoIcon className="h-[23px]" />
              ) : (
                <div className="pt-1.5">
                  <LogoIcon className="h-[18px]" />
                </div>
              )}
            </div>
          </span>
        </div>
      </div>
    </SingletonPopoverProvider>
  );
};

const topBarIconButtonClassName =
  "flex h-8 w-8 items-center justify-center rounded-lg outline-none transition-colors hover:bg-[#333333]";

const CXSeePlansButton = () => {
  const { t } = useTranslations();
  const { isAdmin } = useUserRole();
  const { plans, openSubscriptionDrawer } = useSubscriptionV2();

  if (!isAdmin || !plans?.length) return null;

  return (
    <Tooltip
      renderTrigger={({ props, ref }) => (
        <PlainButton
          ref={ref}
          {...props}
          className={topBarIconButtonClassName}
          onClick={() => openSubscriptionDrawer()}
        >
          <PricingIcon />
        </PlainButton>
      )}
    >
      {t({
        id: "main-nav-bar-component.see-plans",
        defaultMessage: "See Plans",
      })}
    </Tooltip>
  );
};

const CXSupportChatButton = () => {
  const { t } = useTranslations();
  const { isInitialized, show } = useChatBot();

  if (!isInitialized) return null;

  return (
    <Tooltip
      renderTrigger={({ props, ref }) => (
        <PlainButton
          ref={ref}
          {...props}
          className={topBarIconButtonClassName}
          onClick={() => show()}
        >
          <SupportChatIcon />
        </PlainButton>
      )}
    >
      {t({
        id: "main-nav-bar-component.support-chat",
        defaultMessage: "Support Chat",
      })}
    </Tooltip>
  );
};
